import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface MemberProfile {
  id: string;
  email: string;
  role_id: number;
  status: string;
  first_name: string;
  last_name: string;
}

export interface MemberOverviewStats {
  total_courses?: number;
  completed_courses?: number;
  total_certificates?: number;
  learning_streak?: number;
  current_monthly_hours?: number;
  monthly_goal_hours?: number;
  monthly_goal_percentage?: number;
  courses_to_milestone?: number;
  [key: string]: any;
}

export interface MemberOverview {
  profile: MemberProfile;
  stats: MemberOverviewStats;
}

export interface MemberCourse {
  course_id: number;
  title: string;
  status: string;
  progress: number;
  enrolled_date: string | null;
  completed_at: string | null;
  last_activity: string | null;
  lessons_completed: number;
}

export interface MemberAssessment {
  kind: 'submission' | 'pending';
  form_id: number;
  instance_id: number | null;
  title: string;
  status: string;
  assigned_at: string | null;
  due_date: string | null;
  submitted_at: string | null;
  score: number | null;
  max_score: number | null;
  percentage: number | null;
}

export interface MemberCertificate {
  source: 'self' | 'admin';
  id: number;
  certificate_name: string;
  status: string;
  issued_date: string | null;
  expiry_date: string | null;
  link: string | null;
}

export interface MemberHoursDay {
  day: string;
  hours: number;
}

export interface MemberLearningHours {
  daily: MemberHoursDay[];
  totalHours: number;
  days: number;
}

export interface EnrollableCourse {
  id: number;
  title: string;
  description?: string | null;
}

export interface AssignableAssessment {
  id: number;
  title: string;
  description?: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: { message: string } | null;
}

const BASE = '/admin/organization-members';

const wrap = async <T>(call: Promise<ApiResponse<T>>, fallbackErr: string): Promise<ServiceResult<T>> => {
  try {
    const response = await call;
    return {
      success: response.success,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('OrganizationMemberActivityService:', error);
    return {
      success: false,
      error: { message: error?.message ?? fallbackErr }
    };
  }
};

export const getMemberOverview = (userId: string) =>
  wrap<MemberOverview>(httpClient.get(`${BASE}/${userId}/overview`), 'Failed to fetch member overview');

export const getMemberEnrolledCourses = (userId: string) =>
  wrap<MemberCourse[]>(httpClient.get(`${BASE}/${userId}/enrolled-courses`), 'Failed to fetch enrolled courses');

export const getMemberAssessments = (userId: string) =>
  wrap<MemberAssessment[]>(httpClient.get(`${BASE}/${userId}/assessments`), 'Failed to fetch assessments');

export const getMemberCertificates = (userId: string) =>
  wrap<MemberCertificate[]>(httpClient.get(`${BASE}/${userId}/certificates`), 'Failed to fetch certificates');

export const getMemberLearningHours = (userId: string, days = 30) =>
  wrap<MemberLearningHours>(httpClient.get(`${BASE}/${userId}/learning-hours?days=${days}`), 'Failed to fetch learning hours');

export const getEnrollableCourses = (userId: string, search = '') =>
  wrap<EnrollableCourse[]>(
    httpClient.get(`${BASE}/${userId}/enrollable-courses?search=${encodeURIComponent(search)}`),
    'Failed to fetch enrollable courses'
  );

export const getAssignableAssessments = (userId: string, search = '') =>
  wrap<AssignableAssessment[]>(
    httpClient.get(`${BASE}/${userId}/assignable-assessments?search=${encodeURIComponent(search)}`),
    'Failed to fetch assignable assessments'
  );

export const enrollMemberInCourse = (userId: string, courseId: number) =>
  wrap<{ enrollmentId: number; courseId: number; title: string | null }>(
    httpClient.post(`${BASE}/${userId}/enroll`, { courseId }),
    'Failed to enroll member in course'
  );

export const assignAssessmentToMember = (userId: string, formId: number, dueDate: string | null) =>
  wrap<{ instance_id: number; title: string; due_date: string | null }>(
    httpClient.post(`${BASE}/${userId}/assign-assessment`, { formId, dueDate }),
    'Failed to assign assessment'
  );
